import React, { useState } from 'react';
import { Award, Users, Target, TrendingUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import RegisterPopup from '../components/RegisterPopup';
import SEO from '../components/SEO';

const About = () => {
  const [showPopup, setShowPopup] = useState(false);

  return (
    <div>
      <SEO 
        title="About Trademark Flux - Leading Trademark Registration Experts | USA"
        description="Learn about Trademark Flux, America's trusted trademark registration service. Over 15 years of experience, 10,000+ successful registrations, and 98% approval rate. Meet our expert team."
        keywords="about trademark flux, trademark experts, IP attorneys, brand protection specialists, USPTO accredited, trademark services company"
      />
      {/* Hero Section */}
      <section 
        className="relative py-20 bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(37, 99, 235, 0.7)), url('https://images.unsplash.com/photo-1553484771-6e117b648d45?q=80&w=2000')`,
        }}
      >
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              About Trademark Flux
            </h1>
            <p className="text-xl text-white/90">
              Your trusted partner in trademark and intellectual property protection
            </p>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto" data-aos="fade-up">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">
              Our Mission
            </h2>
            <p className="text-lg text-gray-600 mb-6 leading-relaxed">
              At Trademark Flux, we are dedicated to providing exceptional trademark registration and intellectual property protection services to businesses across the United States. Our mission is to make brand protection accessible, affordable, and stress-free for entrepreneurs, startups, and established businesses alike.
            </p>
            <p className="text-lg text-gray-600 mb-6 leading-relaxed">
              We understand that your brand is one of your most valuable assets. That's why we've built a team of experienced professionals who are committed to delivering personalized service and expert guidance throughout the entire trademark registration process.
            </p>
            <p className="text-lg text-gray-600 leading-relaxed">
              Our comprehensive approach ensures that every client receives thorough trademark searches, professional filing services, and ongoing support to maintain and protect their intellectual property rights.
            </p>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center" data-aos="fade-up">
            Our Core Values
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8" data-aos="fade-up" data-aos="zoom-in">
            <div data-aos="zoom-in" className="text-center">
              <div className="bg-blue-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4" data-aos="zoom-in" data-aos="fade-up">
                <Award className="h-8 w-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Excellence</h3>
              <p className="text-gray-600">
                We strive for excellence in every aspect of our service delivery and client relationships
              </p>
            </div>
            <div className="text-center">
              <div className="bg-blue-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4" data-aos="zoom-in" data-aos="fade-up">
                <Users className="h-8 w-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Client-Focused</h3>
              <p className="text-gray-600">
                Your success is our priority. We provide personalized attention to each client's unique needs
              </p>
            </div>
            <div className="text-center">
              <div className="bg-blue-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4" data-aos="zoom-in" data-aos="fade-up">
                <Target className="h-8 w-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Integrity</h3>
              <p className="text-gray-600">
                We operate with transparency, honesty, and ethical practices in all our business dealings
              </p>
            </div>
            <div className="text-center">
              <div className="bg-blue-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4" data-aos="zoom-in" data-aos="fade-up">
                <TrendingUp className="h-8 w-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Innovation</h3>
              <p className="text-gray-600">
                We continuously improve our processes and services to provide the best experience
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Story</h2>
            </div>
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                Trademark Flux was founded with a simple yet powerful vision: to make professional trademark protection accessible to businesses of all sizes. What began as a small practice dedicated to helping local entrepreneurs has grown into one of America's most trusted trademark service providers, serving thousands of clients nationwide.
              </p>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                Over the years, we've witnessed firsthand how proper intellectual property protection can transform businesses. We've helped startups become household names, protected family businesses for generations, and empowered entrepreneurs to confidently build their brands. Each success story reinforces our commitment to excellence and drives us to continually improve our services.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                Today, we combine traditional legal expertise with modern technology to deliver faster, more efficient trademark services without compromising on quality. Our team of USPTO-accredited specialists brings together decades of combined experience, ensuring every client receives the highest level of professional service and support.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Approach Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Approach</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              We believe in combining cutting-edge technology with personalized human expertise
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto" data-aos="fade-up" data-aos="zoom-in">
            <div className="bg-white rounded-xl p-8 shadow-md hover:shadow-xl transition-all" data-aos="zoom-in" data-aos="fade-up" data-aos="fade-up" data-aos="fade-up" data-aos="fade-up" data-aos="fade-up" data-aos="fade-up">
              <div className="bg-blue-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-6" data-aos="zoom-in" data-aos="fade-up">
                <Users className="h-8 w-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">Client-Centric Service</h3>
              <p className="text-gray-600 text-center leading-relaxed">
                Every client receives personalized attention from our experienced team. We take the time to understand your unique business needs and tailor our services accordingly, ensuring you get the protection that's right for your brand.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-md hover:shadow-xl transition-all" data-aos="zoom-in" data-aos="fade-up" data-aos="fade-up" data-aos="fade-up" data-aos="fade-up" data-aos="fade-up" data-aos="fade-up">
              <div className="bg-blue-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-6" data-aos="zoom-in" data-aos="fade-up">
                <Target className="h-8 w-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">Results-Driven</h3>
              <p className="text-gray-600 text-center leading-relaxed">
                Our 98% success rate speaks for itself. We use proven strategies and comprehensive research to maximize your chances of trademark approval, backed by our money-back guarantee for qualifying applications.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-md hover:shadow-xl transition-all" data-aos="zoom-in" data-aos="fade-up" data-aos="fade-up" data-aos="fade-up" data-aos="fade-up" data-aos="fade-up" data-aos="fade-up">
              <div className="bg-blue-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-6" data-aos="zoom-in" data-aos="fade-up">
                <TrendingUp className="h-8 w-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">Continuous Innovation</h3>
              <p className="text-gray-600 text-center leading-relaxed">
                We invest in the latest trademark search technology and legal research tools to stay ahead of industry changes, ensuring our clients always receive cutting-edge protection strategies.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* By The Numbers Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Trademark Flux By The Numbers</h2>
            <p className="text-lg text-gray-600">
              Our track record speaks for itself
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-5xl mx-auto" data-aos="fade-up" data-aos="zoom-in">
            <div className="text-center">
              <div className="bg-blue-50 rounded-2xl p-8 mb-4" data-aos="zoom-in" data-aos="fade-up" data-aos="fade-up" data-aos="fade-up">
                <div className="text-4xl md:text-5xl font-bold text-blue-600 mb-2">10,000+</div>
                <div className="text-gray-700 font-semibold">Trademarks Filed</div>
              </div>
            </div>

            <div className="text-center">
              <div className="bg-blue-50 rounded-2xl p-8 mb-4" data-aos="zoom-in" data-aos="fade-up" data-aos="fade-up" data-aos="fade-up">
                <div className="text-4xl md:text-5xl font-bold text-blue-600 mb-2">98%</div>
                <div className="text-gray-700 font-semibold">Success Rate</div>
              </div>
            </div>

            <div className="text-center">
              <div className="bg-blue-50 rounded-2xl p-8 mb-4" data-aos="zoom-in" data-aos="fade-up" data-aos="fade-up" data-aos="fade-up">
                <div className="text-4xl md:text-5xl font-bold text-blue-600 mb-2">15+</div>
                <div className="text-gray-700 font-semibold">Years Experience</div>
              </div>
            </div>

            <div className="text-center">
              <div className="bg-blue-50 rounded-2xl p-8 mb-4" data-aos="zoom-in" data-aos="fade-up" data-aos="fade-up" data-aos="fade-up">
                <div className="text-4xl md:text-5xl font-bold text-blue-600 mb-2">50+</div>
                <div className="text-gray-700 font-semibold">Industries Served</div>
              </div>
            </div>
          </div>

          <div className="mt-12 max-w-4xl mx-auto">
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-8 md:p-12 text-white text-center" data-aos="zoom-in" data-aos="fade-up" data-aos="zoom-in" data-aos="fade-up" data-aos="fade-up">
              <h3 className="text-2xl md:text-3xl font-bold mb-4">
                Join Thousands of Satisfied Clients
              </h3>
              <p className="text-lg text-blue-100 mb-6 max-w-2xl mx-auto">
                From startups to established enterprises, businesses across America trust Trademark Flux with their intellectual property protection.
              </p>
              <Button
                onClick={() => setShowPopup(true)}
                size="lg"
                className="bg-white text-blue-600 hover:bg-blue-50 px-8 py-4"
              >
                Start Your Application Today
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Our Commitment Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Commitment to You</h2>
              <p className="text-lg text-gray-600">
                We stand behind our services with guarantees that protect your investment
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6" data-aos="fade-up" data-aos="zoom-in">
              <div className="bg-white rounded-xl p-8 shadow-md border-l-4 border-blue-600" data-aos="zoom-in" data-aos="fade-up" data-aos="fade-up" data-aos="fade-up" data-aos="fade-up" data-aos="fade-up" data-aos="fade-up">
                <div className="flex items-start" data-aos="zoom-in">
                  <div className="bg-blue-100 rounded-lg p-3 mr-4" data-aos="fade-up">
                    <Award className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">Quality Guarantee</h3>
                    <p className="text-gray-600 leading-relaxed">
                      We guarantee that every trademark application is prepared with meticulous attention to detail and reviewed by our experienced specialists before filing. If errors occur due to our oversight, we'll correct them at no additional charge.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-8 shadow-md border-l-4 border-blue-600" data-aos="zoom-in" data-aos="fade-up" data-aos="fade-up" data-aos="fade-up" data-aos="fade-up" data-aos="fade-up" data-aos="fade-up">
                <div className="flex items-start" data-aos="zoom-in">
                  <div className="bg-blue-100 rounded-lg p-3 mr-4" data-aos="fade-up">
                    <Users className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">Responsive Support</h3>
                    <p className="text-gray-600 leading-relaxed">
                      Your questions and concerns are our priority. We commit to responding to all inquiries within 24 hours during business days and providing regular updates on your application status throughout the entire process.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-8 shadow-md border-l-4 border-blue-600" data-aos="zoom-in" data-aos="fade-up" data-aos="fade-up" data-aos="fade-up" data-aos="fade-up" data-aos="fade-up" data-aos="fade-up">
                <div className="flex items-start" data-aos="zoom-in">
                  <div className="bg-blue-100 rounded-lg p-3 mr-4" data-aos="fade-up">
                    <Target className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">Transparent Process</h3>
                    <p className="text-gray-600 leading-relaxed">
                      No surprises, no hidden fees. We provide clear explanations of each step in the trademark process, detailed cost breakdowns upfront, and honest assessments of your trademark's registrability.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-8 shadow-md border-l-4 border-blue-600" data-aos="zoom-in" data-aos="fade-up" data-aos="fade-up" data-aos="fade-up" data-aos="fade-up" data-aos="fade-up" data-aos="fade-up">
                <div className="flex items-start" data-aos="zoom-in">
                  <div className="bg-blue-100 rounded-lg p-3 mr-4" data-aos="fade-up">
                    <TrendingUp className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">Long-Term Partnership</h3>
                    <p className="text-gray-600 leading-relaxed">
                      Our relationship doesn't end at registration. We provide ongoing support for renewals, monitoring services, and expansion strategies as your business grows and your trademark protection needs evolve.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
              Why Businesses Choose Us
            </h2>
            <div className="grid md:grid-cols-2 gap-8" data-aos="fade-up" data-aos="zoom-in">
              <div className="bg-blue-50 rounded-lg p-6" data-aos="zoom-in" data-aos="fade-up" data-aos="fade-up" data-aos="fade-up">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Expert Knowledge</h3>
                <p className="text-gray-600">
                  Our team brings extensive experience in trademark law and intellectual property protection, ensuring your application is handled with expertise and precision.
                </p>
              </div>
              <div className="bg-blue-50 rounded-lg p-6" data-aos="zoom-in" data-aos="fade-up" data-aos="fade-up" data-aos="fade-up">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Comprehensive Service</h3>
                <p className="text-gray-600">
                  From initial trademark searches to final registration and ongoing monitoring, we provide end-to-end solutions for all your intellectual property needs.
                </p>
              </div>
              <div className="bg-blue-50 rounded-lg p-6" data-aos="zoom-in" data-aos="fade-up" data-aos="fade-up" data-aos="fade-up">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Transparent Pricing</h3>
                <p className="text-gray-600">
                  No hidden fees or surprise charges. Our pricing is clear and competitive, with packages designed to fit various business needs and budgets.
                </p>
              </div>
              <div className="bg-blue-50 rounded-lg p-6" data-aos="zoom-in" data-aos="fade-up" data-aos="fade-up" data-aos="fade-up">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Dedicated Support</h3>
                <p className="text-gray-600">
                  We're here to answer your questions and provide guidance every step of the way. Your success is our commitment.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-blue-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Protect Your Brand?
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Let us help you secure your trademark and protect your business identity with our professional services.
          </p>
          <Button
            onClick={() => setShowPopup(true)}
            size="lg"
            className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-6 text-lg"
          >
            Get Started Today
          </Button>
        </div>
      </section>

      {showPopup && <RegisterPopup onClose={() => setShowPopup(false)} />}
    </div>
  );
};

export default About;